import { useEffect, useState } from 'react';
import { type CatClassId, DEFAULT_CAT_CLASS_ID, type SectorId } from '@cradle7/shared';
import { isCatClassId } from './catClasses';

const LS_KEY = 'cradle7.userSettings';

export type UserSettings = {
  hapticEnabled: boolean;
  screenShakeEnabled: boolean;
  damageNumbersEnabled: boolean;
  reducedMotion: boolean;
  selectedCatClassId: CatClassId;
  splashSeen: boolean;
  dailyStreakSeenDate: string | null;
  theme: 'cyber' | 'neon' | 'mil';
  unlockedSectors: SectorId[];
  currentSectorId: SectorId;
};

const DEFAULTS: UserSettings = {
  hapticEnabled: true,
  screenShakeEnabled: true,
  damageNumbersEnabled: true,
  reducedMotion: false,
  selectedCatClassId: DEFAULT_CAT_CLASS_ID,
  splashSeen: false,
  dailyStreakSeenDate: null,
  theme: 'cyber',
  unlockedSectors: ['B-1'],
  currentSectorId: 'B-1',
};

function load(): UserSettings {
  if (typeof window === 'undefined') return { ...DEFAULTS };
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    if (typeof parsed.selectedCatClassId !== 'string' || !isCatClassId(parsed.selectedCatClassId)) {
      parsed.selectedCatClassId = DEFAULT_CAT_CLASS_ID;
    }
    if (parsed.theme !== 'cyber' && parsed.theme !== 'neon' && parsed.theme !== 'mil') {
      parsed.theme = 'cyber';
    }
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

let current: UserSettings = load();
const listeners = new Set<(s: UserSettings) => void>();

export function getUserSettings(): UserSettings {
  return current;
}

export function setUserSettings(patch: Partial<UserSettings>): void {
  current = { ...current, ...patch };
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LS_KEY, JSON.stringify(current));
    }
  } catch { /* ignore */ }
  listeners.forEach((fn) => fn(current));
}

export function subscribeUserSettings(fn: (s: UserSettings) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function useUserSettings(): UserSettings {
  const [s, setS] = useState(current);
  useEffect(() => subscribeUserSettings(setS), []);
  return s;
}
